import { useCallback, useEffect, useState } from "react";
import { Save } from "lucide-react";
import { api } from "@/lib/api";
import { t } from "@/lib/i18n";
import { Card } from "@/components/ui/Card";
import { FormMessage, type Feedback } from "@/components/ui/FormMessage";
import { METABASE_SETTINGS_EVENT } from "@/hooks/useMetabaseSettings";
import { DEFAULT_METABASE_SETTINGS, type GenericResponse, type MetabaseSettings } from "@/lib/types";

export function MetabasePage() {
  const [cfg, setCfg] = useState<MetabaseSettings>(DEFAULT_METABASE_SETTINGS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState<Feedback>(null);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      setCfg(await api.get<MetabaseSettings>("/api/settings/metabase"));
    } catch (e) {
      setFeedback({ kind: "error", text: e instanceof Error ? e.message : t("Laden fehlgeschlagen") });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void reload();
  }, [reload]);

  const onSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setFeedback(null);
    try {
      const resp = await api.post<GenericResponse>("/api/settings/metabase", {
        ...cfg,
        url: cfg.url.trim(),
      });
      if (resp.success) {
        setFeedback({ kind: "success", text: resp.message });
        window.dispatchEvent(new Event(METABASE_SETTINGS_EVENT));
        await reload();
      } else {
        setFeedback({ kind: "error", text: resp.message });
      }
    } catch (e) {
      setFeedback({ kind: "error", text: e instanceof Error ? e.message : t("Speichern fehlgeschlagen") });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex flex-col gap-4 p-6">
      <FormMessage feedback={feedback} />

      <Card
        title="Metabase"
        description={t("Zeigt Metabase als Menüpunkt in der Seitenleiste, sichtbar für alle Nutzer. Der Link öffnet in einem neuen Tab.")}
      >
        {loading ? (
          <div className="text-sm text-muted-foreground">{t("Lade…")}</div>
        ) : (
          <form onSubmit={onSave} className="flex flex-col gap-4">
            <label className="flex cursor-pointer items-center gap-2">
              <input
                type="checkbox"
                checked={cfg.enabled}
                onChange={(e) => setCfg({ ...cfg, enabled: e.target.checked })}
                className="h-4 w-4 cursor-pointer accent-primary"
              />
              <span className="text-sm">{t("Metabase im Menü anzeigen")}</span>
            </label>

            <label className="flex max-w-lg flex-col gap-1">
              <span className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                {t("Metabase-URL")}
              </span>
              <input
                type="text"
                value={cfg.url}
                onChange={(e) => setCfg({ ...cfg, url: e.target.value })}
                placeholder="http://leif-server:43000"
                autoComplete="off"
                className="h-9 rounded-md border border-border bg-background px-3 font-mono text-xs outline-none focus:border-primary focus:ring-1 focus:ring-primary"
              />
              <span className="text-[11px] text-muted-foreground">
                {t("Vollständige Adresse inklusive http:// oder https://. Muss aus dem Browser der Nutzer erreichbar sein.")}
              </span>
            </label>

            <div>
              <button
                type="submit"
                disabled={saving}
                className="inline-flex h-9 items-center gap-1.5 rounded-md bg-primary px-3 text-xs font-medium text-primary-foreground disabled:opacity-60"
              >
                <Save className="h-3.5 w-3.5" /> {saving ? t("Speichern…") : t("Einstellungen speichern")}
              </button>
            </div>
          </form>
        )}
      </Card>
    </div>
  );
}
